import React, { forwardRef, useImperativeHandle } from 'react';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import DOMPurify from 'dompurify';
import htmlToPdfMake from 'html-to-pdfmake';
import { fetchClientData } from './api/fetchClient';
import { createCanvas, loadImage } from 'canvas';
import { resolveLegacyHighlightKeywords } from '../utils/highlightKeywords';
import { buildPublicClippingUrl } from '../utils/publicLinks';

if (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) {
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
} else {
  pdfMake.vfs = pdfFonts;
}

// Função para converter URLs de imagens para base64
const convertImageUrlToBase64 = async (imageUrl) => {
  try {
    const proxy = imageUrl.replace('amtex-nxt-media.s3.amazonaws.com', 'cliente.contentclipping.com.br');
    const response = await fetch(proxy);
    if (!response.ok) throw new Error('Network response was not ok');
    const imageBlob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = async () => {
        let base64Data = reader.result;
        if (base64Data.includes('image/webp')) {
          base64Data = await convertWebPToPNG(base64Data);
        }
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(imageBlob);
    });
  } catch (error) {
    console.error('Erro ao converter URL da imagem para base64:', error);
    return null; // Retorna null em caso de erro
  }
};

// Função para converter imagens webp para png
const convertWebPToPNG = async (webPDataUrl) => {
  try {
    const img = await loadImage(webPDataUrl);
    const canvas = createCanvas(img.width, img.height);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Erro ao converter WebP para PNG:', error);
    return webPDataUrl; // Retorna o original em caso de erro
  }
};

const getDataUrl = (url) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.send();
  });
};

const stripInvisibleBreakers = (html) =>
  String(html)
    .replace(/<wbr\s*\/?>/gi, '')
    .replace(/\ufeff|\u200b|\u200c|\u200d|\u2028|\u2029|\u00ad/gu, '');

const applyPdfmakeHighlight = (node, keywords, {
  minLen = 3,
  maxTermLen = 60,
  wholeWord = false,
  maxItemsPerNode = 5000,
} = {}) => {
  if (!node || !Array.isArray(keywords) || keywords.length === 0) return;

  const uniq = Array.from(new Set(
    keywords.map(k => (k ?? '').toString().trim())
            .filter(k => k.length >= minLen && k.length <= maxTermLen)
  ));
  if (!uniq.length) return;

  const escape = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const source = uniq.map(escape).join('|');
  const boundaryStart = wholeWord ? '(?<!\\p{L})' : '';
  const boundaryEnd   = wholeWord ? '(?!\\p{L})' : '';
  const re = new RegExp(`${boundaryStart}(${source})${boundaryEnd}`, wholeWord ? 'giu' : 'gi');

  const highlightRun = (txt) => {
    if (!txt || typeof txt !== 'string') return txt;

    let m, last = 0, out = [], count = 0;
    while ((m = re.exec(txt)) && count < maxItemsPerNode) {
      if (m.index > last) out.push({ text: txt.slice(last, m.index) });
      out.push({ text: m[0], bold: true, background: '#FFEB3B' });
      last = re.lastIndex;
      count++;
    }
    if (last === 0) return txt;
    if (last < txt.length) out.push({ text: txt.slice(last) });
    return out;
  };

  const visit = (n) => {
    if (!n || typeof n !== 'object') return;

    if (typeof n.text === 'string') {
      const res = highlightRun(n.text);
      if (Array.isArray(res)) n.text = res;
    } else if (Array.isArray(n.text)) {
      n.text = n.text.flatMap(run => {
        if (typeof run === 'string') {
          const res = highlightRun(run);
          return Array.isArray(res) ? res : [{ text: res }];
        } else if (run && typeof run.text === 'string') {
          const res = highlightRun(run.text);
          return Array.isArray(res) ? res.map(r => ({ ...run, ...r })) : [{ ...run, text: res }];
        }
        return [run];
      });
    }

    ['stack', 'ol', 'ul', 'columns'].forEach(key => {
      if (Array.isArray(n[key])) n[key].forEach(visit);
    });

    if (Array.isArray(n.table?.body)) {
      n.table.body.forEach(row => Array.isArray(row) && row.forEach(visit));
    }
  };

  if (Array.isArray(node)) node.forEach(visit); else visit(node);
};

const getImageSizeFromDataUrl = async (dataUrl) => {
  try {
    const img = await loadImage(dataUrl);
    return { width: img.width || img.naturalWidth || 0, height: img.height || img.naturalHeight || 0 };
  } catch {
    return { width: 0, height: 0 };
  }
};

const NewsPDFExporter = forwardRef(({ clientId }, ref) => {
  useImperativeHandle(ref, () => ({
    exportPDF: async (newsItems , userTitle = '', userSubtitle = '', sortOption, includeTitleAndSubtitle, includeSummary, logoPosition = 'default', titleFontSize = 14, subtitleFontSize = 10, titleColor = 'gray', showLogoOnCover = true, summaryType = 'resumido', showTermsSummary = false, printedMode = 'image') => {
      // Função para adicionar a página de capa
      const addCoverPage = async (docContent, brandImage, clientData) => {
        const linkClient = process.env.REACT_APP_CLIENT_URL;

        const topImageUrl = 'https://cliente.contentclipping.com.br/media/black-blue-banner.png';
        const bottomImageUrl = 'https://cliente.contentclipping.com.br/media/black-blue-banner2.png';

        const topImageBase64 = await convertImageUrlToBase64(topImageUrl);
        const bottomImageBase64 = await convertImageUrlToBase64(bottomImageUrl);

        const companyName = clientData.company_name;
        const currentDate = getCurrentDate();

        const reportTitle = includeTitleAndSubtitle ? (userTitle || companyName) : ''; 
        const reportSubtitle = includeTitleAndSubtitle ? (userSubtitle || currentDate) : '';

        const reportCoverImage = clientData.report_cover_pdf
        ? clientData.report_cover_pdf.replace(process.env.REACT_APP_AWS_URL, `${linkClient}`)
        : null;

        if (reportCoverImage) {
          const reportCoverImageBase64 = await convertImageUrlToBase64(reportCoverImage);
      
          docContent.push(
            {
              image: reportCoverImageBase64,
              width: 635, // Largura total da página
              height: 842, // Altura total da página
              alignment: 'center',
              absolutePosition: { x: 0, y: 0 }
            },
            {
              stack: [
                { text: reportTitle, style: 'header', fontSize: titleFontSize, color: titleColor, alignment: 'center', absolutePosition: { x: 35, y: 215 } },
                { text: reportSubtitle, style: 'subheader', fontSize: subtitleFontSize, color: titleColor, alignment: 'center', margin: [0, 210, 0, 0] },
                showLogoOnCover && brandImage ? {
                  image: brandImage,
                  width: 120,
                  absolutePosition: logoPosition === 'center'
                    ? { x: (595 - 120) / 2, y: 150 }
                    : { x: 10, y: 20 }
                } : {},
              ],
            },
            { text: '', pageBreak: 'after' }
          );
        } else {
          if (topImageBase64) {
            docContent.push({ image: topImageBase64, width: 635, alignment: 'center', absolutePosition: { x: 0, y: 0 } });
          }
      
          docContent.push(
            {
              stack: [
                { text: reportTitle, style: 'header', fontSize: titleFontSize, color: titleColor, alignment: 'center', absolutePosition: { x: 35, y: 250 } },
                { text: reportSubtitle, style: 'subheader', fontSize: subtitleFontSize, color: titleColor, alignment: 'center', margin: [30, 130, 0, 0] },
                showLogoOnCover && brandImage ? {
                  image: brandImage,
                  width: 120,
                  absolutePosition: logoPosition === 'center'
                    ? { x: (595 - 120) / 2, y: 150 } 
                    : { x: 10, y: 20 }
                } : {},
              ],
              absolutePosition: { x: 0, y: 150 }
            }
          );
      
          if (bottomImageBase64) {
            docContent.push({ image: bottomImageBase64, width: 550, alignment: 'start', absolutePosition: { x: 0, y: 772 } });
          }
      
          docContent.push({ text: '', pageBreak: 'after' });
        }
      };

      const onlyDate = (publishDateStr = '') => (publishDateStr.split(' ')[0] || '').trim();

      const addTableOfContentsPage = (docContent) => {
        docContent.push(
          { toc: { title: { text: 'SUMÁRIO', style: 'tocTitle' }, numberStyle: 'tocNumber', textStyle: 'tocItemTitle' } },
          { text: '', pageBreak: 'after' }
        );
      };

    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  //   const highlightKeywordsInHTML = (
  //   html,
  //   keywords,
  //   {
  //     minLen = 3,
  //     maxTermLen = 60,
  //     highlightsPer1000Chars = 60, 
  //     maxHighlightsTotal = 20000,
  //     maxPerKeyword = 2000,  
  //     maxPerNode = 300,   
  //     wholeWord = false,
  //   } = {}
  // ) => {
  //   if (!html || !Array.isArray(keywords) || keywords.length === 0) return html;

  //   const uniq = Array.from(new Set(
  //     keywords
  //       .map(k => (k ?? '').toString().trim())
  //       .filter(k => k.length >= minLen && k.length <= maxTermLen)
  //   ));
  //   if (uniq.length === 0) return html;

  //   uniq.sort((a, b) => b.length - a.length);

  //   const source = uniq.map(escapeRegex).join('|');
  //   const boundary = wholeWord ? '(?<!\\p{L})' : '';
  //   const boundaryEnd = wholeWord ? '(?!\\p{L})' : '';
  //   const pattern = new RegExp(`${boundary}(${source})${boundaryEnd}`, wholeWord ? 'giu' : 'gi');

  //   const doc = new DOMParser().parseFromString(html, 'text/html');

  //   const walkerSize = doc.createTreewalker?.(doc.body, NodeFilter.SHOW_TEXT, null)
  //                     || doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
  //   let t, totalChars = 0;
  //   while ((t = walkerSize.nextNode())) totalChars += (t.nodeValue || '').length;

  //   const adaptiveLimit = Math.min(
  //     maxHighlightsTotal,
  //     Math.ceil((totalChars / 1000) * highlightsPer1000Chars)
  //   );

  //   const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
  //   const nodes = [];
  //   let n;
  //   while ((n = walker.nextNode())) nodes.push(n);

  //   let totalCount = 0;
  //   const perKeywordCount = new Map();

  //   for (const textNode of nodes) {
  //     if (totalCount >= adaptiveLimit) break;

  //     const text = textNode.nodeValue;
  //     if (!text) continue;

  //     if (!pattern.test(text)) { pattern.lastIndex = 0; continue; }
  //     pattern.lastIndex = 0;

  //     const frag = doc.createDocumentFragment();
  //     let lastIndex = 0;
  //     let m;
  //     let nodeCount = 0;

  //     while ((m = pattern.exec(text))) {
  //       if (totalCount >= adaptiveLimit) break;
  //       if (nodeCount >= maxPerNode) break;

  //       const kw = m[0];
  //       const curr = perKeywordCount.get(kw) || 0;
  //       if (curr >= maxPerKeyword) {
  //         lastIndex = pattern.lastIndex;
  //         continue;
  //       }

  //       const before = text.slice(lastIndex, m.index);
  //       if (before) frag.appendChild(doc.createTextNode(before));

  //       const span = doc.createElement('span');
  //       span.setAttribute('style', 'font-weight:bold; background-color:#FFF59D;');
  //       span.textContent = kw;
  //       frag.appendChild(span);

  //       perKeywordCount.set(kw, curr + 1);
  //       totalCount++;
  //       nodeCount++;
  //       lastIndex = pattern.lastIndex;
  //     }

  //     if (lastIndex === 0) continue;

  //     if (lastIndex < text.length) {
  //       frag.appendChild(doc.createTextNode(text.slice(lastIndex)));
  //     }
  //     textNode.parentNode.replaceChild(frag, textNode);
  //   }

  //   return doc.body.innerHTML;
  // };

  const computeMatchedKeywords = (
    htmlOrText,
    keywords,
    { minLen = 3, maxTermLen = 60, wholeWord = false, maxItems = 200 } = {}
  ) => {
    if (!Array.isArray(keywords) || !keywords.length) return [];

    let text = '';
    if (htmlOrText) {
      if (/<[a-z]/i.test(htmlOrText)) {
        const doc = new DOMParser().parseFromString(htmlOrText, 'text/html');
        text = (doc.body.textContent || '');
      } else {
        text = String(htmlOrText);
      }
    }
    if (!text) return [];

    const uniq = Array.from(new Set(
      keywords.map(k => (k ?? '').toString().trim())
              .filter(k => k.length >= minLen && k.length <= maxTermLen)
    ));
    if (!uniq.length) return [];

    const source = uniq.map(escapeRegex).join('|');
    const boundaryStart = wholeWord ? '(?<!\\p{L})' : '';
    const boundaryEnd   = wholeWord ? '(?!\\p{L})' : '';
    const re = new RegExp(`${boundaryStart}(${source})${boundaryEnd}`, wholeWord ? 'giu' : 'gi');

    const seen = new Set();
    const out = [];
    let m;
    while ((m = re.exec(text)) && out.length < maxItems) {
      const found = (m[0] || '').trim();
      if (!found) continue;
      const key = found.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(found);
      }
    }
    return out;
  };


      const addDetailedSummaryPage = (docContent, items) => {
        docContent.push({ text: 'SUMÁRIO', style: 'tocTitle', margin: [0, 0, 0, 10] });

        const resolveUF = (sd = {}) => {
          if (sd.state) {
            if (typeof sd.state === 'string') return sd.state;
            if (typeof sd.state === 'object') {
              return sd.state.sigla || sd.state.acronym || sd.state.uf || sd.state.name || '-';
            }
          }

          if (sd.city && sd.city.state) {
            const s = sd.city.state;
            if (typeof s === 'string') return s;
            if (typeof s === 'object') {
              return s.sigla || s.acronym || s.uf || s.name || '-';
            }
          }

          return '-';
        };

        const resolveEditorial = (ed) => {
          if (!ed) return '-';
          if (typeof ed === 'string') return ed;
          if (typeof ed === 'object') return ed.name || ed.title || ed.label || '-';
          return '-';
        };

        items.forEach((it) => {
          const destId   = `news-${it.id}`;
          const vehicle  = (it?.source_detail?.name || '').toUpperCase();
          const cats     = (it?.categories || []).map(c => c?.name).filter(Boolean).join(', ') || '-';
          const uf       = resolveUF(it?.source_detail); 
          const dateOnly = onlyDate(it?.publish_date || '');
          const editorial = resolveEditorial(it?.editorial);

         docContent.push({
            columns: [
              { text: it.title || '', linkToDestination: destId, style: 'tocItemTitle', margin: [0, 2, 0, 0] },
              { text: '', pageReference: destId, alignment: 'right', style: 'tocNumber', margin: [0, 2, 0, 0] }
            ]
          });
            docContent.push({
              text: `${vehicle} | ${cats} | ${uf} | ${dateOnly} | ${editorial}`,
              style: 'tocMeta',
              margin: [0, 0, 0, 8]
            });
        });

        docContent.push({ text: '', pageBreak: 'after' });
      };

      const addNewsItemPage = async (docContent, item, brandImage, isLastItem, clientData) => {
        let contentToRender = '';
        let cleanHtmlForSummary = '';
        const highlightKeywords = resolveLegacyHighlightKeywords(item);
        const link = buildPublicClippingUrl(item.id);
        if (item.source_detail.source_type === 'tv' || item.source_detail.source_type === 'radio') {
          const transcript = item.content_text && item.content_text.trim() !== ''
          ? item.content_text
          : '<i>Esta notícia ainda não possui transcrição.</i>';
          contentToRender = `<a href="${link}" target="_blank">Visualização da notícia</a><br/><br/>${transcript}`;
        } else if (item.source_detail.source_type === 'site' || item.source_detail.source_type === 'blog') {
          contentToRender = `<a href="${link}" target="_blank">Visualização da notícia</a><br/><br/>${item.content_text}`;
        }

        // Adiciona conteúdo formatado para tipos diferentes de 'impresso'
        if (item.source_detail.source_type !== 'impresso') {
          const cleanHtmlContent = await removeDuplicateIds(DOMPurify.sanitize(contentToRender, { FORBID_TAGS: ['iframe', 'video', 'script', 'style', 'svg', 'noscript'], FORBID_ATTR: ['style'] }));

          cleanHtmlForSummary = cleanHtmlContent;

          const normalizedHtml = stripInvisibleBreakers(cleanHtmlContent);
          const pdfContent = htmlToPdfMake(normalizedHtml);
          applyPdfmakeHighlight(pdfContent, highlightKeywords, { wholeWord: false });

          // Garantir que pdfContent seja um array
          const contentArray = Array.isArray(pdfContent) ? pdfContent : [pdfContent];

          const reportCoverColor = clientData.report_cover_color; 

          docContent.push(
            // Adiciona o gradiente atrás da imagem da marca
            reportCoverColor
            ? {
                canvas: [
                  {
                    type: 'rect',
                    x: 0,
                    y: 0,
                    w: 600,
                    h: 40,
                    color: reportCoverColor // Usa a cor sólida
                  }
                ],
                absolutePosition: { x: 0, y: 0 }
              }
            : {
                canvas: [
                  {
                    type: 'rect',
                    x: 0,
                    y: 0,
                    w: 600,
                    h: 40,
                    linearGradient: ['#4f1a72', 'white', '#639e9a'] // Usa o gradiente padrão
                  }
                ],
                absolutePosition: { x: 0, y: 0 }
              }
          );

          // if (brandImage) {
          //   docContent.push({ image: brandImage, maxWidth: 100, maxHeight: 40, absolutePosition: { x: 260, y: 5 } });
          // }

          docContent.push(
            { text: `${item.source_detail.name} | ${item.publish_date} | ${item.source_detail.source_type} | ${item.categories.map(cat => cat.name).join(', ')}`.toUpperCase(), style: 'subtitle', alignment: 'center', fontSize: 8 },
            { text: item.title, style: 'title', alignment: 'center', fontSize: 12, bold: true, id: `news-${item.id}`, tocItem: summaryType === 'resumido' },
            {
              canvas: [
                { type: 'line', x1: 0, y1: 10, x2: 595 - 2 * 40, y2: 10, lineWidth: 1, lineColor: '#d3d3d3' }
              ],
              margin: [0, 10]
            }
          );

          if (item.source_detail.source_type === 'tv' && item.thumbnails && item.thumbnails.length > 0) {
            const thumbnailBase64 = await convertImageUrlToBase64(item.thumbnails[0]);
            if (thumbnailBase64) {
              docContent.push({
                image: thumbnailBase64,
                width: 400,
                alignment: 'center',
                margin: [0, 10, 0, 10]
              });
            }
          }

          docContent.push(
            ...contentArray.map((content) => ({ ...content, alignment: 'justify' }))
          );

          if (showTermsSummary) {
          const cats = (item.categories || []).map(c => c?.name).filter(Boolean);
            const matchedKws = computeMatchedKeywords(cleanHtmlForSummary, highlightKeywords, {
              wholeWord: false,
              maxItems: 200
            });
            docContent.push(
              { text: 'RESUMO DOS TERMOS', style: 'termsTitle' },
              { text: ['Assuntos: ',        { text: (cats.length ? cats.join(', ') : '-'),       bold: true }], style: 'termsRow' },
              { text: ['Palavra-chave: ',  { text: (matchedKws.length ? matchedKws.join(', ') : '-'), bold: true }], style: 'termsRow' }
            );
          }

          if (!isLastItem) {
            docContent.push({ text: '', pageBreak: 'after' });
          }
        }

        // Adiciona apenas as imagens para o tipo 'impresso'
        if (item.source_detail.source_type === 'impresso' && item.thumbnails && item.thumbnails.length > 0) {
          const reportCoverColor = clientData.report_cover_color;
          const showPrintedImages = printedMode !== 'text';
          const showPrintedText   = printedMode !== 'image';

          docContent.push(
            // Adiciona o gradiente atrás da imagem da marca
            reportCoverColor
              ? {
                  canvas: [
                    {
                      type: 'rect',
                      x: 0,
                      y: 0,
                      w: 600,
                      h: 40,
                      color: reportCoverColor // Usa a cor sólida
                    }
                  ],
                  absolutePosition: { x: 0, y: 0 }
                }
              : {
                  canvas: [
                    {
                      type: 'rect',
                      x: 0,
                      y: 0,
                      w: 600,
                      h: 40,
                      linearGradient: ['#4f1a72', 'white', '#639e9a'] // Usa o gradiente padrão
                    }
                  ],
                  absolutePosition: { x: 0, y: 0 }
                }
          );

          // if (brandImage) {
          //   docContent.push({ image: brandImage, maxWidth: 100, maxHeight: 40, absolutePosition: { x: 260, y: 10 } });
          // }

          docContent.push(
            { text: `${item.source_detail.name} | ${item.publish_date} | ${item.source_detail.source_type } | ${item.categories.map(cat => cat.name).join(', ')}`.toUpperCase(), style: 'subtitle', alignment: 'center', fontSize: 8 },
            { text: item.title, style: 'title', alignment: 'center', fontSize: 12, bold: true, id: `news-${item.id}`, tocItem: summaryType === 'resumido'},
            {
              canvas: [
                { type: 'line', x1: 0, y1: 10, x2: 595 - 2 * 40, y2: 10, lineWidth: 1, lineColor: '#d3d3d3' }
              ],
              margin: [0, 10]
            }
          );

          docContent.push({
            text: 'Visualização da notícia',
            link: link,
            color: 'blue',
            decoration: 'underline',
            alignment: 'left',
            margin: [0, 5, 0, 10]
          });

          if (showPrintedImages && item.thumbnails && item.thumbnails.length > 0) {
            for (let i = 0; i < item.thumbnails.length; i++) {
                const thumbnailUrl = item.thumbnails[i];
                const thumbnailBase64 = await convertImageUrlToBase64(thumbnailUrl);
                if (thumbnailBase64) {
                  const { width: iw, height: ih } = await getImageSizeFromDataUrl(thumbnailBase64);
                  const MAX_W = 515; 
                  const MAX_H = 600;

                  if ((iw && iw > MAX_W) || (ih && ih > MAX_H)) {
                    const scale = Math.min(
                      iw ? MAX_W / iw : 1,
                      ih ? MAX_H / ih : 1
                    );
                    const w = Math.floor((iw || MAX_W) * scale);
                    const h = Math.floor((ih || MAX_H) * scale);
                    docContent.push({
                      image: thumbnailBase64,
                      width: w,
                      height: h,
                      alignment: 'center',
                      margin: [0, 10, 0, 10],
                    });
                  } else {
                    docContent.push({
                      image: thumbnailBase64,
                      alignment: 'center',
                      margin: [0, 10, 0, 10],
                    });
                  }

                  if (i < item.thumbnails.length - 1) {
                    docContent.push({ text: '', pageBreak: 'after' });
                  }
                }
              }
            }
            
            if (showPrintedText) {
              if (item.content_text) {
                const pre = stripInvisibleBreakers(item.content_text);
                const cleanHtmlContent = await removeDuplicateIds(
                DOMPurify.sanitize(pre, {
                    FORBID_TAGS: ['iframe','video','script','style','svg','noscript'],
                    FORBID_ATTR: ['style']
                  })
                );
                  cleanHtmlForSummary = cleanHtmlContent;

                const pdfContent = htmlToPdfMake(cleanHtmlContent);
                applyPdfmakeHighlight(pdfContent, highlightKeywords, { wholeWord: false });
                const contentArray = Array.isArray(pdfContent) ? pdfContent : [pdfContent];

                if (printedMode === 'image+text' && item.thumbnails?.length) {
                  docContent.push({ text: '', margin: [0, 10] });
                }

                docContent.push(...contentArray.map(content => ({ ...content, alignment: 'justify', margin: [0,5,0,0] })));
              } else if (printedMode === 'text') {
                docContent.push({ text: 'Esta notícia impressa não possui texto disponível.', italics: true, color: '#666', margin: [0,5,0,0] });
              }
            }

           if (showTermsSummary) {
              const cats = (item.categories || []).map(c => c?.name).filter(Boolean);
              const matchedKws = computeMatchedKeywords(cleanHtmlForSummary, highlightKeywords, {
              wholeWord: false,
              maxItems: 200
              });
              docContent.push(
                { text: 'RESUMO DOS TERMOS', style: 'termsTitle' },
              { text: ['Assuntos: ',        { text: (cats.length ? cats.join(', ') : '-'),       bold: true }], style: 'termsRow' },
                { text: ['Palavra-chave: ',  { text: (matchedKws.length ? matchedKws.join(', ') : '-'), bold: true }], style: 'termsRow' }
              );
            }


          if (!isLastItem) {
            docContent.push({ text: '', pageBreak: 'after' });
          }
        }
      };

      try {
        if (!clientId) {
          throw new Error("clientId is not defined");
        }

        const clientData = await fetchClientData(clientId);
        const brandImageUrl = clientData.brand.replace('amtex-nxt-media.s3.us-east-2.amazonaws.com', 'cliente.contentclipping.com.br');
        const brandBase64 = await convertImageUrlToBase64(brandImageUrl);

        let sortedNewsItems = [...newsItems]; 

        const parseDateTime = (dateString) => {
          const [datePart, timePart] = dateString.split(' ');
          const [day, month, year] = datePart.split('/').map(Number);
          const [hours, minutes] = timePart ? timePart.split(':').map(Number) : [0, 0];
          return new Date(year, month - 1, day, hours, minutes);
        };

        if (sortOption === 'dataRecente') {
          sortedNewsItems.sort((a, b) => parseDateTime(b.publish_date) - parseDateTime(a.publish_date));
        } else if (sortOption === 'dataAntiga') {
          sortedNewsItems.sort((a, b) => parseDateTime(a.publish_date) - parseDateTime(b.publish_date));
        } else if (sortOption === 'veiculo') {
          sortedNewsItems.sort((a, b) => {
            const orderA = a.source_detail.order || 0;
            const orderB = b.source_detail.order || 0;
        
            if (orderA === orderB) {
              return a.source_detail.name.localeCompare(b.source_detail.name);
            }
        
            if (orderA === 0) return 1; 
            if (orderB === 0) return -1;
        
            return orderA - orderB;
          });
        } else if (sortOption === 'selecionada') {
            sortedNewsItems = [...newsItems];
        }

        const docContent = [];

        await addCoverPage(docContent, brandBase64, clientData);

        if (includeSummary) {
          if (summaryType === 'resumido') {
            addTableOfContentsPage(docContent);
          } else {
            addDetailedSummaryPage(docContent, sortedNewsItems);
          }
        }

        for (let i = 0; i < sortedNewsItems.length; i++) {
          const item = sortedNewsItems[i];
          const isLastItem = i === sortedNewsItems.length - 1;
          await addNewsItemPage(docContent, item, brandBase64, isLastItem, clientData);
        }

        const docDefinition = {
          content: docContent,
          styles: {
            header: {
              fontSize: 14,
              bold: true,
              color: 'gray'
            },
            subheader: {
              fontSize: 10,
              color: 'gray'
            },
            title: {
              fontSize: 15,
              bold: true,
              color: 'gray'
            },
            subtitle: {
              fontSize: 12,
              margin: [0, 20],
              color: 'gray'
            },
            content: {
              fontSize: 12,
              margin: [0, 10]
            },
            tocTitle: {
              fontSize: 16,
              bold: true,
              alignment: 'center',
              margin: [0, 10],
              color: 'gray'
            },
            tocItemTitle: {
              fontSize: 8.5,
              margin: [0, 10]
            },
            tocNumber: {
              fontSize: 8.5,
              margin: [0, 10]
            },
            tocMeta: {
              fontSize: 8,
              color: '#666'
            },
            kw: {
              bold: true,
              background: '#FFFF00'
            },
            termsTitle: { 
              fontSize: 10, 
              bold: true, 
              color: '#616161', 
              margin: [0, 30, 0, 2],
            },
            termsRow: { 
              fontSize: 9, 
              color: '#616161', 
              margin: [0, 0, 0, 2],
            },
          },
          footer: (currentPage) => {
            return {
              text: currentPage.toString(),
              alignment: 'right',
              fontSize: 10,
              margin: [0, 10, 10, 10],
              color: 'gray'
            };
          }
        };

        pdfMake.createPdf(docDefinition).download(`Clipping-${getCurrentDate()}.pdf`);
      } catch (error) {
        console.error('Erro ao buscar e converter a imagem do brand:', error);
      }
    },
  }));

  // Função para obter a data atual formatada
  const getCurrentDate = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return dd + '/' + mm + '/' + yyyy;
  };

  // Função para remover IDs duplicados e ajustar atributos de imagem
  const removeDuplicateIds = async (htmlContent) => {
    const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
    const allElements = doc.body.getElementsByTagName('*');

    const imgs = Array.from(doc.body.querySelectorAll('img'));
    const imgsToRemove = [];
    
    for (const img of imgs) {
      let src = img.getAttribute('src');
      if (src) {
        if (src.includes('svg+xml')) {
          src = img.getAttribute('data-srcset') ? img.getAttribute('data-srcset').split('?')[0] : null;
        }
        if (src) {
          src = await getDataUrl(src.split('?')[0]);
          img.src = src;

          // Ajustar max-width e max-height
          img.style.maxWidth = '500px';
          img.style.maxHeight = '700px';

          let validation = src.split(';base64')[0];
          if (!validation.includes('png') && !validation.includes('jpg') && !validation.includes('jpeg')) {
            imgsToRemove.push(img);
          }
        } else {
          imgsToRemove.push(img);
        }
      } else {
        imgsToRemove.push(img);
      }
    }

    imgsToRemove.forEach(img => {
      img.parentNode.removeChild(img);
    });

    Array.from(allElements).forEach(async (element) => {
      if (element.id) {
        element.removeAttribute('id');
      }

      if (element.getAttribute('src')) {
        const src = element.getAttribute('src');
        if (src) {
          element.src = await getDataUrl(src.split('?')[0]);
        }
      }
    });

    return doc.body.innerHTML;
  };

  return (
    <div style={{ display: 'none' }}></div>
  );
});

export default NewsPDFExporter;
